// SPDX-License-Identifier: AGPL-3.0-or-later

import {leaveLobby} from '@app/features/table_game/commands/TableGameCommands';
import TableGames from '@app/features/table_game/state/TableGames';
import {takeVoiceSeat} from '@app/features/voice_scene/commands/VoiceSeatCommands';
import {POSITION_STALE_MS, RoomPositions} from '@app/features/voice_scene/net/PositionBuffer';
import type {RoomAvatars, RoomWalk, VoiceSceneHandle} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import VoiceSceneCamera from '@app/features/voice_scene/state/VoiceSceneCamera';
import type {SeatAssignment, SeatParticipant} from '@app/features/voice_scene/utils/SeatAssignment';
import {ROOM_LIMITS} from '@fluxer/constants/src/RoomCharacters';
import {type Room, RoomEvent} from 'livekit-client';
import {useEffect, useState} from 'react';

// A walker's packets are stale this long after the newest one; the room wakes then to sit it back down.
const STALE_WAKE_MS = POSITION_STALE_MS + 100;

// People in the LiveKit room, me included: the one count the walking cap is checked against (the sender and the room's
// listener count the same way); 0 without a room.
export function useRoomParticipantCount(room: Room | null): number {
	const [count, setCount] = useState(0);
	useEffect(() => {
		if (!room) {
			setCount(0);
			return;
		}
		const read = (): void => setCount(room.remoteParticipants.size + 1);
		read();
		room.on(RoomEvent.ParticipantConnected, read);
		room.on(RoomEvent.ParticipantDisconnected, read);
		return () => {
			room.off(RoomEvent.ParticipantConnected, read);
			room.off(RoomEvent.ParticipantDisconnected, read);
		};
	}, [room]);
	return count;
}

// My walk in the room while walking is on there (room_walk_enabled, characters drawn by `layer`): mounted on the
// renderer for my own identity, handed the seats I may sit on and whether the room is past the walking cap,
// registered for the F shortcut. A position the room's one listener accepted (it drops rubbish, the blocked and
// everything past the walking cap) wakes the room so that walk is drawn. null while walking is off: nothing is
// mounted, listened to or sent. One timer per room, set again by every accepted position, wakes the room when the
// walkers' packets go stale (an end packet is lossy): a quiet walker sits back down even where nothing else draws.
export function useRoomWalk({
	handle,
	room,
	channelId,
	enabled,
	layer,
	assignment,
	games,
	myUserId,
	participantCount,
}: {
	handle: VoiceSceneHandle;
	room: Room | null;
	channelId: string;
	enabled: boolean;
	layer: RoomAvatars | null;
	assignment: SeatAssignment<SeatParticipant>;
	games: boolean;
	myUserId: string | null;
	participantCount: number;
}): RoomWalk | null {
	const identity = room?.localParticipant.identity ?? null;
	const [walk, setWalk] = useState<RoomWalk | null>(null);
	useEffect(() => {
		if (!enabled || !layer || !identity) return;
		const mounted = handle.mountWalk?.({identity, takeSeat: (seat) => void takeVoiceSeat(channelId, seat)}) ?? null;
		if (!mounted) return;
		setWalk(mounted);
		const offShortcut = VoiceSceneCamera.registerWalk(mounted);
		return () => {
			offShortcut();
			mounted.dispose();
			setWalk(null);
		};
	}, [handle, enabled, layer, identity, channelId]);

	useEffect(() => {
		if (!walk) return;
		const mine = myUserId !== null ? (assignment.placements.get(myUserId)?.seat ?? null) : null;
		// A table seat comes with its game (the table panel), a watch seat only with games on.
		const seats = handle.layout.seats.filter(
			(seat) => !seat.table && (!seat.watch || games) && (seat.name === mine || !assignment.seated.has(seat.name)),
		);
		walk.setSeats(seats, mine);
	}, [walk, handle, assignment, games, myUserId]);

	// Getting up from a table nobody has started is leaving it: left alone it stays open for its 10 minute lobby. A
	// running game stays: leaving loses it, and that is the table panel's confirmed Leave.
	useEffect(() => {
		if (!walk || myUserId === null) return;
		return walk.onWalkingChange(() => {
			if (!walk.getPose()) return;
			const mine = assignment.placements.get(myUserId)?.seat;
			const slot = handle.layout.seats.find((seat) => seat.name === mine)?.table?.slot;
			if (slot && TableGames.getSummary(channelId, slot)?.status === 'lobby') void leaveLobby(channelId, slot);
		});
	}, [walk, handle, assignment, myUserId, channelId]);

	const crowded = participantCount > ROOM_LIMITS.posMaxParticipants;
	useEffect(() => {
		walk?.setCrowded(crowded);
	}, [walk, crowded]);

	useEffect(() => {
		if (!walk) return;
		let stale: ReturnType<typeof setTimeout> | null = null;
		const off = RoomPositions.subscribe(() => {
			walk.othersMoved();
			if (stale !== null) clearTimeout(stale);
			stale =
				RoomPositions.size > 0
					? setTimeout(() => {
							stale = null;
							walk.othersMoved();
						}, STALE_WAKE_MS)
					: null;
		});
		return () => {
			off();
			if (stale !== null) clearTimeout(stale);
		};
	}, [walk]);

	return walk;
}
